import { Component, HostListener } from '@angular/core';
import { GlobalService } from './service/global.service';
import {AuthService} from "./services/accounts/auth.service";
import {RbacService} from "./services/accounts/rbac.service";
import {NavigationEnd, Router} from "@angular/router";
import { filter, skip } from 'rxjs/operators';
import { clearChunkReloadFlag } from './global-error-handler';

/** Routes that must always be reachable signed-out and always render bare, with no
 *  sidebar/header shell and no signed-in identity — regardless of whether the visitor
 *  happens to already hold a valid session token in this browser. */
const PUBLIC_ROUTE_PREFIXES = ['/accounts', '/home', '/activate', '/reset-password'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {

  title = 'buupass';
  isauth: boolean =  false;
  isAuthenticated: boolean = false;
  isMobile = false;
  /** Authenticated AND not on a route that must always render bare (see PUBLIC_ROUTE_PREFIXES). */
  showAppShell: boolean = false;

  constructor(
    public global: GlobalService,
    public auth:AuthService,
    public rbac: RbacService,
    public  route: Router
  ) {
    this.auth.isAuthenticated()
  }

  /** Reads the real browser address bar (window.location), not Router.url. Router.url only
   *  reflects the CURRENT route once the router's own (async) navigation has resolved — on a
   *  fresh load it's still '/' for a brief window while the initial route/lazy chunk is being
   *  resolved. window.location is correct from the very first synchronous tick, so checks made
   *  before/independent of the router's navigation lifecycle (e.g. during ngOnInit, or when a
   *  background 401 arrives while a slow/dev-mode chunk is still loading) never race it. */
  private isPublicRoute(url: string = window.location.pathname): boolean {
    return PUBLIC_ROUTE_PREFIXES.some(prefix => url.includes(prefix));
  }

  private updateShellVisibility() {
    this.showAppShell = this.isAuthenticated && !this.isPublicRoute();
  }

  ngOnInit() {
    // Check initial authentication state
    const initialAuth = this.auth.isAuthenticated();
    this.isAuthenticated = initialAuth;
    this.updateShellVisibility();

    // A signed-out visitor landing on a protected route (e.g. bare '/') gets sent to
    // /accounts. isPublicRoute() reads window.location, not Router.url, so — unlike the
    // isAuthenticatedSubject replay below — this is safe to act on immediately: it can't
    // race the router's own (async) resolution of the initial navigation.
    if (!initialAuth && !this.isPublicRoute()) {
      this.route.navigate(['/accounts']);
    }

    // Public routes never need an RBAC profile — skip the fetch entirely there so a stale
    // token can't trigger a background 401 (and logout()) while the visitor is on e.g.
    // /activate, which would otherwise race the page's own navigation/render.
    if (initialAuth && !this.rbac.getSnapshot() && !this.isPublicRoute()) {
      this.rbac.fetchMe().subscribe({
        error: (err) => console.error('Error fetching RBAC profile:', err)
      });
    }

    // skip(1): a BehaviorSubject replays its current value synchronously on subscribe —
    // that replay fires here before the router's initial navigation has resolved. Acting on
    // it would wrongly treat a public route as protected and redirect straight to /accounts,
    // stomping the in-flight initial navigation. The replayed value is already handled above
    // via initialAuth/updateShellVisibility(), so only real subsequent transitions
    // (login/logout) need to run this redirect check.
    this.auth.isAuthenticatedSubject.pipe(skip(1)).subscribe((value) => {
      // Only redirect to accounts if not authenticated and not already on a public route —
      // activate/reset-password/home must stay reachable signed-out, same as accounts itself.
      if (value === false && !this.isPublicRoute()) {
        this.route.navigate(['/accounts'])
      }
      this.isAuthenticated = value;
      this.updateShellVisibility();
    });

    // The shell must also disappear when navigating to a public route while already signed
    // in (e.g. opening an activation link in a tab where you're still logged in as someone
    // else) — isAuthenticated alone doesn't change on navigation, so listen separately.
    this.route.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      this.updateShellVisibility();
    });

    this.checkScreenWidth()
    /**if (this.isauth == false || this.isauth == null || this.isauth == undefined) {
          this.global.redirect('accounts', false)
    }**/

    // The app rendered without hitting a stale-chunk failure — clear the one-shot reload
    // guard so a genuine chunk-load error later in this same tab can still recover itself.
    setTimeout(() => clearChunkReloadFlag(), 5000);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenWidth();
  }

  checkScreenWidth() {
    this.isMobile = window.innerWidth < 768;
  }

}
